/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { ComponentProps, memo } from 'react';

import { ButtonBase } from '@mui/material';

import { DatasetCategory } from '../../types/dataset/DatasetCategory';
import { ArrowRoundedIcon } from '../svg-icons/ArrowRoundedIcon';
import { useDataSetCategoriesListStyles } from './DataSetCategoriesList.styles';

// eslint-disable-next-line react-memo/require-memo
function defaultGetOptionLabel<V>(option: V) {
  return `${option}`;
}

// eslint-disable-next-line react-memo/require-memo
function defaultGetOptionSelected(option: any, selected: any) {
  return option === selected;
}

// eslint-disable-next-line react-memo/require-memo
function defaultGetOptionThumbnail() {
  return undefined;
}

// eslint-disable-next-line react-memo/require-memo
function defaultGetOptionCount() {
  return undefined;
}

type CategoriesListProps<V, C> = {
  options: V[];
  selected: C;
  className?: string;
  itemClassName?: string;
  title?: string;
  getOptionLabel?: (option: V) => string;
  getOptionSelected?: (option: V, selected: C) => boolean;
  getOptionThumbnail?: (option: V) => string | undefined;
  getOptionCount?: (option: V, selected: C) => [number] | [number, number] | undefined;
  isDisabled?: boolean;
  onItemClick: (event: React.MouseEvent<HTMLButtonElement>, value: V) => any;
  noOptionsText?: string | React.ReactNode;
  buttonProps?: ComponentProps<typeof ButtonBase>;
};

// eslint-disable-next-line react-memo/require-memo
const DataSetCategoriesListComponent = <V extends DatasetCategory, C = V>(props: CategoriesListProps<V, C>) => {
  const {
    className = '',
    title,
    itemClassName = '',
    options,
    getOptionLabel = defaultGetOptionLabel,
    getOptionSelected = defaultGetOptionSelected,
    getOptionThumbnail = defaultGetOptionThumbnail,
    getOptionCount = defaultGetOptionCount,
    selected,
    onItemClick,
    noOptionsText,
    isDisabled,
    buttonProps = {},
  } = props;
  const classes = useDataSetCategoriesListStyles();
  return (
    <div className={cx(classes.root, className)}>
      {title && <div className={classes.title}>{title}</div>}
      {options.length > 0 ? (
        <div className={classes.list}>
          {options.map((o, i) => {
            const thumbnail = getOptionThumbnail(o);
            const count = getOptionCount(o, selected);
            return (
              <ButtonBase
                key={i}
                {...buttonProps}
                className={cx(classes.item, itemClassName, {
                  [classes.itemActive]: getOptionSelected(o, selected),
                  [classes.itemWithThumbnail]: thumbnail,
                })}
                onClick={event => onItemClick(event, o)}
                disabled={isDisabled}
              >
                {thumbnail && (
                  <div className={classes.itemThumbnail}>
                    <img src={thumbnail} alt="thumbnail" />
                  </div>
                )}
                <div className={classes.itemTitle}>{getOptionLabel(o)}</div>
                <div className={classes.itemBadges}>
                  {count && <div className={classes.itemCount}>{count.join('/')}</div>}

                  <div className={classes.itemArrow}>
                    <ArrowRoundedIcon />
                  </div>
                </div>
              </ButtonBase>
            );
          })}
        </div>
      ) : (
        noOptionsText && <div className={classes.noOptionsText}>{noOptionsText}</div>
      )}
    </div>
  );
};

export const DataSetCategoriesList = memo(DataSetCategoriesListComponent);
